import React, { useRef, useState, useEffect } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Link } from "react-router-dom";
import JSONPretty from "react-json-pretty";
import Loader from "../../components/utils/Loader";
import { getCustomerSchema, getTransactionSchema } from "../../components/actions/schema";
import { createTransaction } from "../../components/actions/campaign";
import DashboardLayout from "../../components/layouts/DashboardLayout";

function PostTransaction() {
  const code = sessionStorage.getItem("code");
  const clientId = sessionStorage.getItem("code");

  const [state, setState] = useState({
    responseCopied: false,
    requestCopied: false,
    loading: false,
    err: false,
    errMsg: "",
    responseText: "",
    disable: false,
  });

  const [requestData, setRequestData] = useState({
    customerDetails: {},
    transactionDetails: {},
  });
  const [loadings, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const customerSchemaRes = await getCustomerSchema(code);
      const transactionSchemaRes = await getTransactionSchema(clientId);
      const customerSchema = customerSchemaRes?.data || [];
      const transactionSchema = transactionSchemaRes?.data || [];

      const customerDetails = customerSchema.reduce((acc, item) => {
        if (item.ident === "1") {
          const defaultValue =
            item.fieldtype === "integer" || item.fieldtype === "float" ? 0 : "";
          acc[item.jsonfield] = defaultValue;
        }
        return acc;
      }, {});

      const transactionDetails = transactionSchema.reduce((acc, item) => {
        const defaultValue =
          item.DataType === "integer" || item.DataType === "float" ? 0 : "";
        acc[item.jsonfield] = defaultValue;
        return acc;
      }, {});

      setRequestData({ customerDetails, transactionDetails });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setState({
        ...state,
        err: true,
        errMsg: error.message || "Failed to fetch schema",
      });
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  const requestText = JSON.stringify(requestData, null, 2);
  const { requestCopied, responseCopied, loading, responseText, disable } = state;

  const handleCopy = (copy) => {
    if (copy === "request") {
      setState({ ...state, requestCopied: true });
      setTimeout(() => setState({ ...state, requestCopied: false }), 1000);
    } else {
      setState({ ...state, responseCopied: true });
      setTimeout(() => setState({ ...state, responseCopied: false }), 1000);
    }
  };

  const handleRequestChange = (e) => {
    try {
      const updatedData = JSON.parse(e.target.value);
      setRequestData(updatedData);
    } catch (error) {
      // Ignore invalid JSON
    }
  };

  const OnSubmit = async () => {
    setState({ ...state, loading: true, disable: true });
    try {
      const response = await createTransaction(code, requestData);
      setState({
        ...state,
        loading: false,
        disable: false,
        responseText: JSON.stringify(response, null, 2),
      });
    } catch (error) {
      setState({
        ...state,
        loading: false,
        disable: false,
        err: true,
        errMsg: error.message || "An error occurred",
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {loadings && <Loader />}

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/transactions"
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-white rounded-xl transition-all duration-200 cursor-pointer shadow-sm border border-gray-200"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </Link>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Post Transaction</h2>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
            {/* Request Section */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Request</h3>
                <div className="flex items-center gap-3">
                  <CopyToClipboard text={requestText} onCopy={() => handleCopy("request")}>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
                      <i
                        className={`ri-file-copy-line ${
                          requestCopied ? "text-green-600" : ""
                        }`}
                      ></i>
                      {requestCopied ? "Copied!" : "Copy"}
                    </button>
                  </CopyToClipboard>

                  <button
                    onClick={OnSubmit}
                    disabled={disable || loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    {loading ? "Sending..." : "Send"}
                  </button>
                </div>
              </div>

              <textarea
                value={requestText}
                onChange={handleRequestChange}
                className="w-full h-96 p-4 font-mono text-sm bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                spellCheck={false}
              />
            </div>

            {/* Response Section */}
            <div className="p-6 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Response</h3>
                {responseText && (
                  <CopyToClipboard text={responseText} onCopy={() => handleCopy("response")}>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
                      <i
                        className={`ri-file-copy-line ${
                          responseCopied ? "text-green-600" : ""
                        }`}
                      ></i>
                      {responseCopied ? "Copied!" : "Copy"}
                    </button>
                  </CopyToClipboard>
                )}
              </div>

              <div className="relative bg-gray-50 rounded-xl border border-gray-300 p-4 min-h-96 overflow-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-96">
                    <Loader />
                  </div>
                ) : responseText ? (
                  <JSONPretty
                    data={responseText}
                    theme={{
                      main: "line-height:1.5;color:#1a1a1a;background:#f9fafb;overflow:auto;padding:16px;border-radius:12px;",
                      key: "color:#1a1a1a;font-weight:400;",
                      string: "color:#22a55d;",
                      value: "color:#d33;",
                      boolean: "color:#0066cc;",
                      number: "color:#d33;",
                      null: "color:#777;font-style:italic;",
                      error: "color:#f33;",
                    }}
                  />
                ) : (
                  <p className="text-gray-400 text-center mt-20">
                    Click play to get response
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {state.err && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-3">
            <i className="ri-error-warning-line text-xl"></i>
            <span>{state.errMsg}</span>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default PostTransaction;
